import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getGetHuntQueryOptions,
  getGetMyHuntProgressQueryOptions,
  completeChallenge,
  type PublicChallenge,
  type PublicChallengeType,
  type CompleteChallengeRequestEvidence,
  ApiError
} from '@cdf/api-client-react';
import { type HuntSession, huntAuthHeaders } from './use-hunt-session';
import { safeStorageLength, safeStorageKey, safeGetItem } from '@/lib/safe-storage';
import { useChallengeToast } from '@/components/hunt/challenge-toast';

const STORAGE_PREFIX = 'hunt-session:';

/**
 * Scans localStorage for any active hunt session. Participants are in one
 * hunt at a time, so the first valid session found is used.
 */
function findActiveSession(): HuntSession | null {
  for (let i = 0; i < safeStorageLength(); i++) {
    const key = safeStorageKey(i);
    if (key?.startsWith(STORAGE_PREFIX)) {
      const raw = safeGetItem(key);
      if (raw) {
        try {
          const parsed = JSON.parse(raw) as HuntSession;
          if (parsed?.token && parsed?.huntSlug) return parsed;
        } catch {
          // corrupt entry — skip
        }
      }
    }
  }
  return null;
}

/**
 * Lightweight hook that exposes the active hunt session and incomplete
 * challenges to feature pages (report wizard, AR overlay, map explorer).
 *
 * When no hunt session exists, all return values are null/empty and
 * `completeChallenge` is a no-op — so non-hunt users see zero behavioral
 * change.
 */
export function useHuntChallenges() {
  const [session, setSession] = useState<HuntSession | null>(() => findActiveSession());
  const queryClient = useQueryClient();
  const { showChallengeToast } = useChallengeToast();

  // Re-scan when the page gains focus (e.g. returning from another tab)
  useEffect(() => {
    const handler = () => setSession(findActiveSession());
    handler();
    window.addEventListener('focus', handler);
    return () => window.removeEventListener('focus', handler);
  }, []);

  const slug = session?.huntSlug ?? '';

  // Hunt state (public, no auth needed)
  const huntQuery = useQuery({
    ...getGetHuntQueryOptions(slug),
    enabled: !!slug
  });

  // My progress (requires participant token)
  const progressQuery = useQuery({
    ...getGetMyHuntProgressQueryOptions(slug, {
      request: session ? huntAuthHeaders(session.token) : undefined
    }),
    enabled: !!slug && !!session,
    refetchInterval: 15_000
  });

  const completedIds = useMemo(
    () => new Set(progressQuery.data?.completedChallengeIds ?? []),
    [progressQuery.data?.completedChallengeIds]
  );

  const incompleteChallenges = useMemo<PublicChallenge[]>(() => {
    const all = huntQuery.data?.challenges ?? [];
    return all.filter((c) => !completedIds.has(c.id));
  }, [huntQuery.data?.challenges, completedIds]);

  const incompleteByType = useMemo(() => {
    const map: Partial<Record<PublicChallengeType, PublicChallenge>> = {};
    for (const c of incompleteChallenges) {
      if (!map[c.type]) map[c.type] = c;
    }
    return map;
  }, [incompleteChallenges]);

  const completeMutation = useMutation({
    mutationFn: async ({
      challengeId,
      evidence
    }: {
      challengeId: string;
      evidence?: CompleteChallengeRequestEvidence;
    }) => {
      if (!session) throw new Error('No hunt session');
      return completeChallenge(
        session.huntSlug,
        challengeId,
        { evidence: evidence ?? null },
        huntAuthHeaders(session.token)
      );
    },
    onSuccess: (data) => {
      showChallengeToast({
        title: 'Challenge complete!',
        points: data.pointsAwarded
      });
      // Invalidate progress + leaderboard so passport and live board refresh
      queryClient.invalidateQueries({ queryKey: ['getMyHuntProgress'] });
      queryClient.invalidateQueries({ queryKey: ['getHuntLeaderboard'] });
    },
    onError: (error: unknown) => {
      // 409 = already completed — silently ignore
      if (error instanceof ApiError && error.status === 409) return;
      // Other errors: swallow silently to avoid disrupting the host page
    }
  });

  const completeChallengeCb = useCallback(
    (challengeId: string, evidence?: CompleteChallengeRequestEvidence) => {
      // Don't fire if already completed or currently mutating
      if (completedIds.has(challengeId)) return;
      if (completeMutation.isPending) return;
      completeMutation.mutate({ challengeId, evidence });
    },
    [completedIds, completeMutation]
  );

  return {
    session,
    hunt: huntQuery.data,
    progress: progressQuery.data,
    incompleteChallenges,
    incompleteByType,
    completeChallenge: completeChallengeCb,
    isCompleting: completeMutation.isPending
  };
}
